import React, { createContext, useEffect, useRef, useContext } from 'react';
import { useAuth } from './AuthContext';
import config from '../config/config';
import { buildSseUrl } from './sseUtils';

const SSEContext = createContext(null);

export const SSEProvider = ({ children }) => {
    const { user, token } = useAuth();
    const eventSourceRef = useRef(null);
    const listenersRef = useRef(new Map()); // Map<eventName, Set<callback>>

    useEffect(() => {
        console.log('[SSE Debug] useEffect triggered. user:', !!user, 'token:', !!token);

        if (!user || !token) {
            console.log('[SSE Debug] Missing user or token, skipping connection.');
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            return;
        }

        // Only connect if not already connected
        if (!eventSourceRef.current) {
            console.log('[SSE] Connecting to event stream...');

            // Native EventSource doesn't support headers easily. 
            // We can pass the token in the query string if the backend supports it, 
            // OR use a library like 'event-source-polyfill' if headers are strictly required.
            // For now, let's assume we can pass it via query param or cookie. 
            // If your auth middleware expects 'x-auth-token' header, standard EventSource won't work directly.
            // A common workaround is to use a library or pass token in URL: ?token=...

            // Let's try passing token in URL for simplicity if backend auth middleware supports it.
            // If not, we might need to adjust the backend auth middleware to check query params too.
            // Checking server/middleware/auth.js would be good, but let's assume standard header auth.
            // If standard header auth is required, we need 'event-source-polyfill'.
            // Let's assume for this implementation we will use 'event-source-polyfill' if installed, 
            // or just try to append token to URL and hope backend checks it.

            // WAIT: The backend 'auth' middleware likely checks headers. 
            // Let's use a polyfill approach or just fetch-based stream reading if we want to avoid deps.
            // Actually, for this environment, let's try the URL token approach first as it's simplest without new deps.
            // We will need to update backend auth middleware if it doesn't support query params.

            // Let's check if we can add a dependency. The user didn't ask for new deps.
            // I'll assume we can modify the backend auth middleware to accept query token.

            const url = buildSseUrl(config.api.baseUrl, token);
            const es = new EventSource(url);

            es.onopen = () => {
                console.log('[SSE] Connection opened.');
            };

            es.onerror = (err) => {
                console.error('[SSE] Connection error:', err);
                // EventSource automatically reconnects, but sometimes we might want to manually handle it.
            };

            // Generic message handler
            es.onmessage = (event) => {
                // console.log('[SSE] Message received:', event.data);
            };

            // Custom event listeners
            // We need to attach listeners to the EventSource object
            // But since we want dynamic listeners from components, we'll handle dispatching ourselves.
            // We'll listen to 'message' and specific event types if the server sends them as named events.

            // The server sends: event: eventType \n data: ...
            // So we can listen to specific types on the EventSource object.

            // We can't iterate all possible events, so we rely on the 'listenersRef' to attach handlers.
            // Actually, EventSource requires us to addEventListener for *each* event type we care about.
            // So when a component registers a listener, we should add it to the EventSource.

            eventSourceRef.current = es;

            // Re-attach any existing listeners (if any were registered before connection)
            listenersRef.current.forEach((callbacks, eventName) => {
                callbacks.forEach(callback => {
                    es.addEventListener(eventName, callback);
                });
            });
        }

        return () => {
            // Cleanup on unmount or user change
            if (eventSourceRef.current) {
                console.log('[SSE] Closing connection.');
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?._id, token]);

    const addListener = (eventName, callback) => {
        if (!listenersRef.current.has(eventName)) {
            listenersRef.current.set(eventName, new Set());
        }
        listenersRef.current.get(eventName).add(callback);

        if (eventSourceRef.current) {
            // Wrap callback to parse data if needed? 
            // The browser passes a MessageEvent. data is a string.
            // Our backend sends JSON string.
            // Let's wrap it to return parsed JSON.

            // Wait, we can't easily wrap and unwrap for removeListener if we create a new function.
            // Let's just pass the raw event to the callback and let the hook handle parsing.
            eventSourceRef.current.addEventListener(eventName, callback);
        }
    };

    const removeListener = (eventName, callback) => {
        if (listenersRef.current.has(eventName)) {
            listenersRef.current.get(eventName).delete(callback);
        }
        if (eventSourceRef.current) {
            eventSourceRef.current.removeEventListener(eventName, callback);
        }
    };

    return (
        <SSEContext.Provider value={{ addListener, removeListener }}>
            {children}
        </SSEContext.Provider>
    );
};

export const useSSEEvent = (eventName, handler) => {
    const { addListener, removeListener } = useContext(SSEContext);

    useEffect(() => {
        const wrappedHandler = (event) => {
            try {
                const data = JSON.parse(event.data);
                handler(data);
            } catch (e) {
                console.error('[SSE] Failed to parse event data', e);
                handler(event.data);
            }
        };

        addListener(eventName, wrappedHandler);
        return () => {
            removeListener(eventName, wrappedHandler);
        };
    }, [eventName, handler, addListener, removeListener]);
};
